// File: src/services/categoryService.ts
import apiClient from './api';
import { Category } from '../types';

/**
 * Fetches available categories for the current user.
 * This includes system default categories and categories created by the user (client or coach).
 * @param {object} params - Optional query parameters.
 * @param {number} params.skip - Number of records to skip (for pagination).
 * @param {number} params.limit - Maximum number of records to return.
 * @param {boolean} params.include_archived - Whether to include archived categories.
 * @returns {Promise<Category[]>} A promise that resolves to an array of categories.
 */
const getCategories = async (
    params: { skip?: number; limit?: number; include_archived?: boolean } = {}
): Promise<Category[]> => {
  const { skip = 0, limit = 100, include_archived = false } = params;
  const { data } = await apiClient.get<Category[]>('/categories/', {
    params: { skip, limit, include_archived },
  });
  return data;
};

/**
 * Creates a new category for the current user (client or coach).
 * @param {Omit<Category, 'id' | 'client_user_id' | 'coach_user_id' | 'is_system_default' | 'created_at' | 'updated_at'>} payload
 *        The category data to create. `name` is required. `color_tag`, `icon_name`, `is_archived` are optional.
 * @returns {Promise<Category>} A promise that resolves to the created category.
 */
export interface CategoryCreatePayload {
  name: string;
  color_tag?: string | null;
  icon_name?: string | null;
  is_archived?: boolean; // Defaults to false on backend if not provided
}
const createCategory = async (payload: CategoryCreatePayload): Promise<Category> => {
    const { data } = await apiClient.post<Category>('/categories/', payload);
    return data;
};

/**
 * Updates an existing category owned by the current user.
 * @param {number} categoryId - The ID of the category to update.
 * @param {Partial<Omit<Category, 'id' | 'client_user_id' | 'coach_user_id' | 'is_system_default' | 'created_at' | 'updated_at'>>} payload
 *        The category data to update. Fields are optional.
 * @returns {Promise<Category>} A promise that resolves to the updated category.
 */
export interface CategoryUpdatePayload {
  name?: string;
  color_tag?: string | null;
  icon_name?: string | null;
  is_archived?: boolean;
}
const updateCategory = async (categoryId: number, payload: CategoryUpdatePayload): Promise<Category> => {
    const { data } = await apiClient.put<Category>(`/categories/${categoryId}`, payload);
    return data;
};

/**
 * Deletes a category owned by the current user.
 * System default categories cannot be deleted by users.
 * @param {number} categoryId - The ID of the category to delete.
 * @returns {Promise<void>} A promise that resolves when the category is deleted.
 */
const deleteCategory = async (categoryId: number): Promise<void> => {
    await apiClient.delete(`/categories/${categoryId}`);
};


export default {
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
};